import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { toast } from 'sonner';
import {
  PaperPlaneTilt, FloppyDisk, X, CircleNotch, Tag, Lightning,
  PlugsConnected, FileText, Eye, CheckCircle, XCircle, Clock,
  ArrowClockwise, ArrowSquareOut, Info
} from '@phosphor-icons/react';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'text-gray-400 bg-gray-500/10 border-gray-500/20', icon: FileText },
  submitted: { label: 'Submitted', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', icon: PaperPlaneTilt },
  in_review: { label: 'In Review', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20', icon: Eye },
  approved: { label: 'Approved', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', icon: CheckCircle },
  published: { label: 'Published', color: 'text-green-400 bg-green-500/10 border-green-500/20', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'text-red-400 bg-red-500/10 border-red-500/20', icon: XCircle },
  unpublished: { label: 'Unpublished', color: 'text-gray-400 bg-gray-500/10 border-gray-500/20', icon: Clock },
};

const SubmissionModal = ({ isOpen, onClose, agentId, agentConfig, onSubmissionChange }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingSubmission, setExistingSubmission] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    short_description: '',
    category: '',
    tags: '',
    capabilities: '',
    integrations: '',
    difficulty_level: 'intermediate',
    setup_notes: '',
  });

  useEffect(() => {
    if (!isOpen || !agentId) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/marketplace/submissions/by-agent/${agentId}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.submission) {
            setExistingSubmission(data.submission);
            setForm({
              name: data.submission.name || '',
              description: data.submission.description || '',
              short_description: data.submission.short_description || '',
              category: data.submission.category || '',
              tags: (data.submission.tags || []).join(', '),
              capabilities: (data.submission.capabilities || []).join(', '),
              integrations: (data.submission.integrations || []).join(', '),
              difficulty_level: data.submission.difficulty_level || 'intermediate',
              setup_notes: data.submission.agent_config?.setup_notes || '',
            });
            return;
          }
        }

        // Pre-fill from agent config
        const caps = agentConfig?.required_capabilities || [];
        const bindings = agentConfig?.capability_bindings || {};
        const integs = Object.values(bindings).map(b => b?.provider_id).filter(Boolean);

        setForm({
          name: agentConfig?.name || '',
          description: agentConfig?.description || agentConfig?.short_description || '',
          short_description: agentConfig?.description?.substring(0, 120) || '',
          category: agentConfig?.capability_profile?.replace(/_/g, ' ') || '',
          tags: '',
          capabilities: caps.join(', '),
          integrations: [...new Set(integs)].join(', '),
          difficulty_level: caps.length > 3 ? 'advanced' : caps.length > 1 ? 'intermediate' : 'beginner',
          setup_notes: '',
        });
        setExistingSubmission(null);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [isOpen, agentId, agentConfig]);

  const toArray = (str) => str ? str.split(',').map(s => s.trim()).filter(Boolean) : [];

  const handleSaveDraft = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      const body = {
        template_id: agentId,
        name: form.name,
        description: form.description,
        short_description: form.short_description,
        category: form.category,
        tags: toArray(form.tags),
        capabilities: toArray(form.capabilities),
        integrations: toArray(form.integrations),
        difficulty_level: form.difficulty_level,
        agent_config: form.setup_notes ? { setup_notes: form.setup_notes } : null,
        submit_now: false,
      };

      let res;
      if (existingSubmission && ['draft', 'rejected'].includes(existingSubmission.status)) {
        res = await fetch(`${BACKEND_URL}/api/marketplace/submissions/${existingSubmission.submission_id}`, {
          method: 'PUT', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else if (!existingSubmission) {
        res = await fetch(`${BACKEND_URL}/api/marketplace/submissions`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        toast.error(`Cannot edit submission in '${existingSubmission.status}' status`);
        setSubmitting(false);
        return;
      }

      if (res.ok) {
        const data = await res.json();
        setExistingSubmission(data.submission);
        toast.success('Draft saved');
        onSubmissionChange?.(data.submission);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch { toast.error('Network error'); }
    finally { setSubmitting(false); }
  };

  const handleSubmitForReview = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      // Save/create first, then submit
      if (!existingSubmission) {
        await handleSaveDraft();
        // Wait for state update
        setSubmitting(true);
      }

      const subId = existingSubmission?.submission_id;
      if (!subId) {
        // Create + submit directly
        const body = {
          template_id: agentId,
          name: form.name,
          description: form.description,
          short_description: form.short_description,
          category: form.category,
          tags: toArray(form.tags),
          capabilities: toArray(form.capabilities),
          integrations: toArray(form.integrations),
          difficulty_level: form.difficulty_level,
          agent_config: form.setup_notes ? { setup_notes: form.setup_notes } : null,
          submit_now: true,
        };
        const res = await fetch(`${BACKEND_URL}/api/marketplace/submissions`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          const data = await res.json();
          setExistingSubmission(data.submission);
          toast.success('Submitted for marketplace review!');
          onSubmissionChange?.(data.submission);
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Failed to submit');
        }
      } else {
        // Update if draft/rejected, then submit
        if (['draft', 'rejected'].includes(existingSubmission.status)) {
          const updateBody = {
            name: form.name,
            description: form.description,
            short_description: form.short_description,
            category: form.category,
            tags: toArray(form.tags),
            capabilities: toArray(form.capabilities),
            integrations: toArray(form.integrations),
            difficulty_level: form.difficulty_level,
          };
          await fetch(`${BACKEND_URL}/api/marketplace/submissions/${subId}`, {
            method: 'PUT', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updateBody),
          });
        }

        const res = await fetch(`${BACKEND_URL}/api/marketplace/submissions/${subId}/submit`, {
          method: 'POST', credentials: 'include',
        });
        if (res.ok) {
          const updated = { ...existingSubmission, status: 'submitted' };
          setExistingSubmission(updated);
          toast.success('Submitted for marketplace review!');
          onSubmissionChange?.(updated);
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Failed to submit');
        }
      }
    } catch { toast.error('Network error'); }
    finally { setSubmitting(false); }
  };

  if (!isOpen) return null;

  const status = existingSubmission?.status;
  const canEdit = !status || status === 'draft' || status === 'rejected';
  const canSubmit = !status || status === 'draft' || status === 'rejected';
  const statusCfg = status ? STATUS_CONFIG[status] : null;
  const StatusIcon = statusCfg?.icon;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#12131a] border border-white/10 rounded-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="submission-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <h2 className="text-base font-semibold text-white">Marketplace Submission</h2>
            <p className="text-xs text-slate-400 mt-0.5">Submit this automation for marketplace review</p>
          </div>
          <div className="flex items-center gap-2">
            {statusCfg && (
              <span data-testid="submission-status-badge" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${statusCfg.color}`}>
                <StatusIcon size={12} weight="bold" />
                {statusCfg.label}
              </span>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"><X size={18} /></button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><CircleNotch size={24} className="animate-spin text-indigo-400" /></div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Review Feedback for rejected */}
            {status === 'rejected' && existingSubmission?.review_notes && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl" data-testid="rejection-feedback">
                <p className="text-[10px] text-red-300 uppercase font-semibold mb-1">Review Feedback</p>
                <p className="text-xs text-red-200">{existingSubmission.review_notes}</p>
                <p className="text-[10px] text-red-300/60 mt-1">Update and resubmit when ready.</p>
              </div>
            )}

            {/* Published Notice */}
            {status === 'published' && (
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-xl flex items-start gap-2" data-testid="published-notice">
                <CheckCircle size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-green-200 font-medium">Published on Marketplace</p>
                  <p className="text-[10px] text-green-300/60 mt-0.5">This automation is live. Edits will not affect the public listing until re-approved.</p>
                </div>
              </div>
            )}

            {/* Pending Notice */}
            {(status === 'submitted' || status === 'in_review') && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-start gap-2" data-testid="pending-notice">
                <Info size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-blue-200 font-medium">{status === 'submitted' ? 'Awaiting Review' : 'Under Review'}</p>
                  <p className="text-[10px] text-blue-300/60 mt-0.5">Your submission is being reviewed by the platform team.</p>
                </div>
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Name *</label>
                <input data-testid="sub-field-name" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} disabled={!canEdit} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/50 disabled:opacity-50" />
              </div>
              <div>
                <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Short Description</label>
                <input data-testid="sub-field-short-desc" value={form.short_description} onChange={e => setForm(f => ({...f, short_description: e.target.value}))} disabled={!canEdit} maxLength={120} placeholder="Brief summary for marketplace listing" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/50 disabled:opacity-50" />
              </div>
              <div>
                <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Description</label>
                <textarea data-testid="sub-field-desc" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} disabled={!canEdit} rows={3} placeholder="Detailed description of what this automation does" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/50 disabled:opacity-50 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Category</label>
                  <input data-testid="sub-field-category" value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} disabled={!canEdit} placeholder="e.g. Communication" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/50 disabled:opacity-50" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Difficulty</label>
                  <select data-testid="sub-field-difficulty" value={form.difficulty_level} onChange={e => setForm(f => ({...f, difficulty_level: e.target.value}))} disabled={!canEdit} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-indigo-500/50 disabled:opacity-50">
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Tags <span className="text-slate-600">(comma separated)</span></label>
                <input data-testid="sub-field-tags" value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))} disabled={!canEdit} placeholder="email, automation, replies" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/50 disabled:opacity-50" />
              </div>
              <div>
                <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Integrations <span className="text-slate-600">(comma separated)</span></label>
                <input data-testid="sub-field-integrations" value={form.integrations} onChange={e => setForm(f => ({...f, integrations: e.target.value}))} disabled={!canEdit} placeholder="email, webhook, slack" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/50 disabled:opacity-50" />
              </div>
              <div>
                <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Setup Notes <span className="text-slate-600">(optional)</span></label>
                <textarea data-testid="sub-field-setup" value={form.setup_notes} onChange={e => setForm(f => ({...f, setup_notes: e.target.value}))} disabled={!canEdit} rows={2} placeholder="Any setup instructions for users installing this automation" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/50 disabled:opacity-50 resize-none" />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-3 border-t border-white/10">
              <a href="/my-submissions" className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors flex items-center gap-1" data-testid="view-all-submissions-link">
                <ArrowSquareOut size={12} />My Submissions
              </a>
              <div className="flex items-center gap-2">
                {canEdit && (
                  <button data-testid="sub-save-draft" onClick={handleSaveDraft} disabled={submitting || !form.name.trim()} className="flex items-center gap-1.5 px-3 py-2 bg-white/5 border border-white/10 text-white rounded-lg text-xs font-medium hover:bg-white/10 transition-colors disabled:opacity-50">
                    {submitting ? <CircleNotch size={12} className="animate-spin" /> : <FloppyDisk size={12} />}
                    Save Draft
                  </button>
                )}
                {canSubmit && (
                  <button data-testid="sub-submit-review" onClick={handleSubmitForReview} disabled={submitting || !form.name.trim()} className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg text-xs font-medium hover:from-indigo-400 hover:to-purple-400 transition-colors disabled:opacity-50 shadow-lg shadow-indigo-500/25">
                    {submitting ? <CircleNotch size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} />}
                    {status === 'rejected' ? 'Resubmit' : 'Submit for Review'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmissionModal;
